"use client"
import { ContentIcon } from "@/components/ContentIcon"
import { DailyIcon } from "@/components/icons/DailyIcon"
import { IndustriesIcon } from "@/components/icons/IndustriesIcon"
import { InnovationsIcon } from "@/components/icons/InnovationsIcon"
import { ReportsIcon } from "@/components/icons/ReportsIcon"
import { StatsIcon } from "@/components/icons/StatsIcon"
import { TrendsIcon } from "@/components/icons/TrendsIcon"
import { Button } from "@/components/ui/Button"
import { IconButton } from "@/components/ui/IconButton"
import { Input } from "@/components/ui/Inputs"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs"
import type {
  AlgoliaDaily,
  AlgoliaIndex,
  AlgoliaIndustry,
  AlgoliaInnovation,
  AlgoliaMacrotrend,
  AlgoliaMegatrend,
  AlgoliaReport,
  AlgoliaStat,
  AlgoliaSubtrend,
} from "@/lib/algolia"
import { identifyUser, searchClient } from "@/lib/algolia"
import { IS_PRODUCTION, env } from "@/lib/env"
import { useMe } from "@/lib/hooks/use-me"
import { useActivity } from "@/lib/hooks/useActivity"
import { useEventListener } from "@/lib/hooks/useEventListener"
import { useLocalStorage } from "@/lib/hooks/useLocalStorage"
import { useRecentPages } from "@/lib/hooks/useRecentPages"
import { createAssetUrl } from "@/lib/s3"
import { REPORT_TYPE } from "@/lib/static/report-type"
import { SUBTREND_BG_COLOR } from "@/lib/static/subtrend"
import { cn } from "@/lib/tailwind"
import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import type { Hit } from "instantsearch.js"
import type { SendEventForHits } from "instantsearch.js/es/lib/utils"
import { ArrowRight, Search, X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import * as React from "react"
import { Configure, Hits, Index, InfiniteHits, InstantSearch, useInstantSearch, useSearchBox } from "react-instantsearch"
import { GlobalSearchRecentPageItem } from "./GlobalSearchRecentPageItem"

dayjs.extend(advancedFormat)
const RECENT_SEARCHES_KEY = `tw.recent.searches${IS_PRODUCTION ? "" : `.${env.NEXT_PUBLIC_APP_ENV}`}`

export function GlobalSearch() {
  const { data: me } = useMe()
  const searchParams = useSearchParams()
  const defaultOpen = searchParams?.get("query") !== null

  const [isOpen, setIsOpen] = React.useState(defaultOpen)

  React.useEffect(() => {
    if (!me) return
    identifyUser(me.id)
  }, [me])

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
    setIsOpen(defaultOpen)
  }, [defaultOpen])

  if (!isOpen) return null
  return (
    <InstantSearch
      insights
      future={{ preserveSharedStateOnUnmount: true }}
      searchClient={searchClient}
      indexName="innovations" // Confirmed - this IS needed for it to work
    >
      <div className="fixed top-nav right-0 bottom-0 left-0 overflow-y-scroll bg-background">
        <div className="mx-auto w-full max-w-6xl bg-background py-4">
          <SearchInput onClose={() => setIsOpen(false)} />
          <SearchContent />
        </div>
      </div>
    </InstantSearch>
  )
}

function SearchInput({ onClose }: { onClose: () => void }) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const initialQuery = searchParams?.get("query") as string | undefined

  const handleClose = () => {
    clear()
    router.push(`?`)
    onClose()
  }
  useEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      handleClose()
    }
  })
  const { refine, clear } = useSearchBox()

  const [recentSearches, setRecentSearches] = useLocalStorage<string[]>(RECENT_SEARCHES_KEY, [])
  const searchActivity = useActivity()

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.target as HTMLFormElement)
    const query = (formData.get("query") as string | undefined) || ""
    if (query) {
      const filteredRecentSearches = recentSearches.filter((s) => s.toLowerCase() !== query.toLowerCase()).slice(0, 10)
      setRecentSearches([query.toLowerCase(), ...filteredRecentSearches])
      searchActivity({ type: "GLOBAL_SEARCH", content: query.toLocaleLowerCase() })
    }
    router.push(`?query=${encodeURIComponent(query)}`)
  }

  React.useEffect(() => {
    refine(initialQuery || "")
  }, [initialQuery])

  return (
    <form onSubmit={handleSubmit} className="relative flex w-full flex-1 items-center space-x-2">
      <div className="w-full">
        <Input
          defaultValue={initialQuery || ""}
          autoFocus
          variant="solid"
          placeholder="Search TrendWatching"
          name="query"
          className="w-full px-[41px] placeholder:font-normal"
        />
        <div className="absolute top-0 bottom-0 left-1 flex items-center justify-center">
          <IconButton
            type="submit"
            size="sm"
            aria-label="search"
            variant="ghost"
            className="rounded-md"
            icon={<Search size={16} />}
          />
        </div>
      </div>

      <IconButton onClick={handleClose} size="md" aria-label="close search" variant="secondary" icon={<X size={16} />} />
    </form>
  )
}

function SearchContent() {
  const { query } = useSearchBox()
  const [recentPages] = useRecentPages()
  const [recentSearches, setRecentSearches] = useLocalStorage<string[]>(RECENT_SEARCHES_KEY, [])
  const searchActivity = useActivity()
  const router = useRouter()

  const [tabIndex, setTabIndex] = React.useState("all")

  const onRecentSearchClick = React.useCallback(
    (newSearch: string) => {
      if (newSearch) {
        if (newSearch !== query) {
          const filteredRecentSearches = recentSearches.filter((s) => s.toLowerCase() !== newSearch.toLowerCase()).slice(0, 10)
          setRecentSearches([newSearch.toLowerCase(), ...filteredRecentSearches])
          searchActivity({ type: "GLOBAL_SEARCH", content: newSearch.toLocaleLowerCase() })
        }
        router.push(`${window.location.pathname}?query=${encodeURIComponent(newSearch)}`)
      }
    },
    [recentSearches],
  )

  const handleRemoveSearch = (removeSearch: string) => {
    const newSearches = recentSearches.filter((s) => s !== removeSearch)
    setRecentSearches(newSearches)
  }
  const containerRef = React.useRef<HTMLDivElement>(null)

  return (
    <div>
      {query ? (
        <NoResultsBoundaryGlobal>
          <div ref={containerRef} className="h-[calc(100vh-54px)] overflow-y-scroll pb-20">
            <div className="px-2 pt-8 pb-5 text-3xl">
              Results for <strong>{query}</strong>
            </div>
            <Tabs className="w-min" defaultValue="all" value={tabIndex} onValueChange={setTabIndex}>
              <div className="max-w-[95vw] overflow-x-auto overflow-y-hidden">
                <TabsList>
                  <TabsTrigger className={cn(tabIndex === "all" && "text-primary-700")} value="all">
                    All
                  </TabsTrigger>
                  <TabsTrigger className={cn(tabIndex === "daily" && "text-primary-700")} value="daily">
                    <DailyIcon isSelected={tabIndex === "daily"} />
                    <span className="pl-1">Daily</span>
                  </TabsTrigger>
                  <TabsTrigger className={cn(tabIndex === "innovations" && "text-primary-700")} value="innovations">
                    <InnovationsIcon isSelected={tabIndex === "innovations"} />
                    <span className="pl-1">Innovations</span>
                  </TabsTrigger>
                  <TabsTrigger className={cn(tabIndex === "stats" && "text-primary-700")} value="stats">
                    <StatsIcon isSelected={tabIndex === "stats"} />
                    <span className="pl-1">Stats</span>
                  </TabsTrigger>
                  <TabsTrigger className={cn(tabIndex === "reports" && "text-primary-700")} value="reports">
                    <ReportsIcon isSelected={tabIndex === "reports"} />
                    <span className="pl-1">Reports</span>
                  </TabsTrigger>
                  <TabsTrigger className={cn(tabIndex === "subtrends" && "text-primary-700")} value="subtrends">
                    <TrendsIcon isSelected={tabIndex === "subtrends"} />
                    <span className="pl-1">Sub-Trends</span>
                  </TabsTrigger>
                  <TabsTrigger className={cn(tabIndex === "megatrends" && "text-primary-700")} value="megatrends">
                    <TrendsIcon isSelected={tabIndex === "megatrends"} />
                    <span className="pl-1">Mega-Trends</span>
                  </TabsTrigger>
                  <TabsTrigger className={cn(tabIndex === "macrotrends" && "text-primary-700")} value="macrotrends">
                    <TrendsIcon isSelected={tabIndex === "macrotrends"} />
                    <span className="pl-1">Macro-Trends</span>
                  </TabsTrigger>
                  <TabsTrigger className={cn(tabIndex === "industries" && "text-primary-700")} value="industries">
                    <IndustriesIcon isSelected={tabIndex === "industries"} />
                    <span className="pl-1">Industries</span>
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all">
                <AllContent
                  setTabIndex={(index) => {
                    containerRef.current?.scrollTo({ top: 0 })
                    setTabIndex(index)
                  }}
                />
              </TabsContent>
              <TabsContent value="daily">
                <ContentIndex index="daily" component={DailyHit} />
              </TabsContent>
              <TabsContent value="innovations">
                <ContentIndex index="innovations" component={InnovationHit} />
              </TabsContent>
              <TabsContent value="stats">
                <ContentIndex index="stats" component={StatHit} />
              </TabsContent>
              <TabsContent value="reports">
                <ContentIndex index="reports" component={ReportHit} />
              </TabsContent>
              <TabsContent value="subtrends">
                <ContentIndex index="subtrends" component={SubtrendHit} />
              </TabsContent>
              <TabsContent value="megatrends">
                <ContentIndex index="megatrends" component={MegatrendHit} />
              </TabsContent>
              <TabsContent value="macrotrends">
                <ContentIndex index="macrotrends" component={MacrotrendHit} />
              </TabsContent>
              <TabsContent value="industries">
                <ContentIndex index="industries" component={IndustryHit} />
              </TabsContent>
            </Tabs>
          </div>
        </NoResultsBoundaryGlobal>
      ) : (
        <div className="grid h-full grid-cols-1 gap-10 py-4 md:grid-cols-2">
          <div className="col-span-1 max-h-[80vh] space-y-4 overflow-y-auto">
            <p className="font-semibold text-3xl">Recent searches</p>
            <div>
              {recentSearches.slice(0, 10).map((searchTerm) => (
                <div key={searchTerm} className="flex items-center space-x-1">
                  <Button variant="ghost" className="w-full justify-start" onClick={() => onRecentSearchClick(searchTerm)}>
                    <p className="line-clamp-1 text-ellipsis">{searchTerm}</p>
                  </Button>
                  <div className="flex items-center justify-center">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      onClick={() => handleRemoveSearch(searchTerm)}
                      aria-label="remove search"
                      icon={<X size={16} />}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {recentPages.length > 0 && (
            <div className="col-span-1 max-h-[80vh] space-y-4 overflow-y-auto">
              <p className="font-semibold text-3xl">Recently viewed</p>

              {recentPages.slice(0, 10).map((page) => (
                <GlobalSearchRecentPageItem key={page.id} page={page} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ContentIndex({
  index,
  hitsPerPage = 25,
  component,
}: {
  index: string
  hitsPerPage?: number
  component: React.JSXElementConstructor<{
    hit: Hit<any>
    sendEvent: SendEventForHits
  }>
}) {
  const { status } = useInstantSearch()

  return (
    <Index indexName={index}>
      <Configure enableReRanking hitsPerPage={hitsPerPage} clickAnalytics analytics advancedSyntax />
      <NoResultsBoundary index={index} component={component}>
        <InfiniteHits
          showPrevious={false}
          escapeHTML={false}
          hitComponent={component}
          classNames={{
            list: "space-y-3 p-4",
            loadMore: cn(
              "mt-4 rounded-full border border-gray-300 px-4 py-2 font-semibold hover:border-black hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-400 dark:hover:bg-gray-700",
              status !== "idle" && "cursor-wait opacity-70 hover:opacity-70",
            ),
            disabledLoadMore: "hidden",
          }}
        />
      </NoResultsBoundary>
    </Index>
  )
}

interface ContentHit<T extends { [key: string]: any }> {
  __queryID?: string | undefined
  __position?: number
  _highlightResult?: {
    [Property in keyof T]: { value: string; matchedWords: string[]; matchLevel: "full" | "none" | "partial" } | undefined
  }
  _snippetResult?: {
    [Property in keyof T]: { value: string; matchLevel: "full" | "none" | "partial" } | undefined
  }
}

function AllContentIndex({
  index,
  title,
  hitsPerPage = 4,
  component,
  setTabIndex,
}: {
  index: AlgoliaIndex
  title: string
  hitsPerPage?: number
  component: React.JSXElementConstructor<{
    hit: Hit<any>
    sendEvent: SendEventForHits
  }>
  setTabIndex: () => void
}) {
  const { scopedResults } = useInstantSearch()
  const resultCount = scopedResults.find((result) => result.indexId === index)?.results?.nbHits || 0

  return (
    <Index indexName={index}>
      <Configure enableReRanking hitsPerPage={hitsPerPage} clickAnalytics analytics advancedSyntax />
      <NoResultsBoundary shouldShowNoResults index={index} component={component}>
        <div>
          <p className="mb-2 font-bold text-lg">{title}</p>
          <Hits escapeHTML={false} classNames={{ list: "space-y-3" }} hitComponent={component} />
        </div>
        {resultCount > hitsPerPage ? (
          <Button className="rounded-full" variant="outline" leftIcon={<ArrowRight size={16} />} size="sm" onClick={setTabIndex}>
            See all
          </Button>
        ) : null}
        <hr />
      </NoResultsBoundary>
    </Index>
  )
}

function AllContent({ setTabIndex }: { setTabIndex: React.Dispatch<React.SetStateAction<string>> }) {
  return (
    <div className="w-full space-y-6">
      <AllContentIndex title="Daily" index="daily" component={DailyHit} setTabIndex={() => setTabIndex("daily")} />
      <AllContentIndex
        title="Innovations"
        index="innovations"
        component={InnovationHit}
        setTabIndex={() => setTabIndex("innovations")}
      />
      <AllContentIndex title="Stats" index="stats" component={StatHit} setTabIndex={() => setTabIndex("stats")} />
      <AllContentIndex title="Reports" index="reports" component={ReportHit} setTabIndex={() => setTabIndex("reports")} />
      <AllContentIndex
        title="Sub-Trends"
        index="subtrends"
        component={SubtrendHit}
        setTabIndex={() => setTabIndex("subtrends")}
      />
      <AllContentIndex
        title="Mega-Trends"
        index="megatrends"
        component={MegatrendHit}
        setTabIndex={() => setTabIndex("megatrends")}
      />
      <AllContentIndex
        title="Macro-Trends"
        index="macrotrends"
        component={MacrotrendHit}
        setTabIndex={() => setTabIndex("macrotrends")}
      />
      <AllContentIndex
        title="Industries"
        index="industries"
        component={IndustryHit}
        setTabIndex={() => setTabIndex("industries")}
      />
    </div>
  )
}

function InnovationHit(props: { hit: AlgoliaInnovation & ContentHit<AlgoliaInnovation> }) {
  const { hit: innovation } = props

  return (
    <Link
      className="flex items-center space-x-2 hover:opacity-75"
      href={`/innovations/${innovation.slug}?queryId=${props.hit.__queryID}`}
    >
      <Image
        className="max-h-[100px] min-h-[100px] min-w-[150px] max-w-[150px] rounded-lg bg-gray-50 object-cover"
        width={150}
        height={100}
        alt="inno cover"
        src={innovation.cover || ""}
      />

      <div className="font-sans">
        <p className="font-bold text-primary-500">{innovation.businessGoals?.[0]}</p>
        <p
          dangerouslySetInnerHTML={{ __html: innovation._highlightResult?.title?.value || innovation.title }}
          className="line-clamp-1 font-bold text-xl"
        />
        <p
          dangerouslySetInnerHTML={{
            __html: innovation._snippetResult?.description?.value.replace(/&nbsp;/g, " ") || "",
          }}
          className="line-clamp-2"
        />
        <div className="flex items-center space-x-1 text-sm opacity-75">
          <p>{dayjs.unix(innovation.publishedAt).format("MM/YYYY")}</p>
          <p>∙</p>
          {innovation.industry && (
            <>
              <p>{innovation.industry}</p>
              <p>∙</p>
            </>
          )}
          <p
            dangerouslySetInnerHTML={{
              __html: innovation._highlightResult?.brand?.value || innovation.brand,
            }}
            className="line-clamp-1"
          />
        </div>
      </div>
    </Link>
  )
}
function SubtrendHit(props: { hit: AlgoliaSubtrend & ContentHit<AlgoliaSubtrend> }) {
  const { hit: subtrend } = props
  return (
    <Link
      className="flex items-center space-x-2 hover:opacity-75"
      href={`/subtrends/${subtrend.slug}?queryId=${props.hit.__queryID}`}
    >
      <div className="w-max">
        <ContentIcon icon={createAssetUrl(subtrend.iconImage)} bgColor={SUBTREND_BG_COLOR} size={100} className="p-3" />
      </div>
      <div>
        <p
          className="whitespace-nowrap font-bold text-xl leading-5"
          dangerouslySetInnerHTML={{ __html: subtrend._highlightResult?.title?.value || subtrend.title }}
        />
        <p
          dangerouslySetInnerHTML={{
            __html: subtrend._highlightResult?.subTitle?.value || subtrend.subTitle,
          }}
          className="line-clamp-1 font-medium text-lg"
        />
        <p
          dangerouslySetInnerHTML={{
            __html:
              subtrend._snippetResult?.definition?.value.replace("IN BRIEF", "") ||
              subtrend._snippetResult?.description?.value ||
              "",
          }}
          className="line-clamp-1"
        />
        <p className="text-sm">{dayjs.unix(subtrend.publishedAt).format("D MMM YYYY")}</p>
      </div>
    </Link>
  )
}
function MegatrendHit(props: { hit: AlgoliaMegatrend & ContentHit<AlgoliaMegatrend> }) {
  const { hit: megatrend } = props
  return (
    <Link
      className="flex items-center space-x-2 hover:opacity-75"
      href={`/megatrends/${megatrend.slug}?queryId=${props.hit.__queryID}`}
    >
      <div className="w-max">
        <ContentIcon
          icon={createAssetUrl(megatrend.iconImage)}
          bgColor={megatrend.color || "80C4A9"}
          size={100}
          className="p-3"
        />
      </div>
      <div>
        <p
          className="whitespace-nowrap font-bold text-xl"
          dangerouslySetInnerHTML={{ __html: megatrend._highlightResult?.title?.value || megatrend.title }}
        />
        <p
          dangerouslySetInnerHTML={{
            __html: megatrend._highlightResult?.subTitle?.value || megatrend.subTitle,
          }}
          className="line-clamp-1 font-medium text-lg"
        />
        <p
          dangerouslySetInnerHTML={{
            __html:
              megatrend._snippetResult?.definition?.value.replace("IN BRIEF", "") ||
              megatrend._snippetResult?.description?.value ||
              "",
          }}
          className="line-clamp-1"
        />
      </div>
    </Link>
  )
}
function MacrotrendHit(props: { hit: AlgoliaMacrotrend & ContentHit<AlgoliaMacrotrend> }) {
  const { hit: macrotrend } = props
  return (
    <Link
      className="flex items-center space-x-1 hover:opacity-75"
      href={`/macrotrends/${macrotrend.slug}?queryId=${props.hit.__queryID}`}
    >
      <div className="w-max">
        <ContentIcon icon={macrotrend.icon} size={100} className="p-0" />
      </div>
      <div>
        <p
          className="whitespace-nowrap font-bold text-xl"
          dangerouslySetInnerHTML={{ __html: macrotrend._highlightResult?.title?.value || macrotrend.title }}
        />
        <p
          dangerouslySetInnerHTML={{
            __html: macrotrend._highlightResult?.subTitle?.value || macrotrend.subTitle || "",
          }}
          className="line-clamp-1 font-medium text-lg"
        />
        <p
          dangerouslySetInnerHTML={{
            __html: macrotrend._snippetResult?.description?.value || "",
          }}
          className="line-clamp-1"
        />
      </div>
    </Link>
  )
}

function StatHit(props: { hit: AlgoliaStat & ContentHit<AlgoliaStat> }) {
  const { hit: stat } = props

  const letter = React.useMemo(() => (["a", "b", "c", "d", "e", "f"] as const)[Math.floor(Math.random() * 5)], [])

  return (
    <Link
      className="flex items-center space-x-2 hover:opacity-75"
      href={`/stats/${stat.objectID}?queryId=${props.hit.__queryID}`}
    >
      <div
        className={cn(
          "mr-1",
          "flex max-h-[100px] min-h-[100px] min-w-[150px] max-w-[150px] items-center justify-center rounded-lg bg-gradient-to-br p-2",
          letter === "a" && "from-purple-200 to-yellow-50",
          letter === "b" && "from-pink-300 to-blue-50",
          letter === "c" && "from-green-400 to-orange-200",
          letter === "d" && "from-blue-400 to-pink-300",
          letter === "e" && "from-purple-300 to-green-300",
          letter === "f" && "from-yellow-300 to-blue-200",
        )}
      >
        <span
          className={cn(
            "mr-1",
            "line-clamp-3 whitespace-nowrap text-center font-bold text-4xl text-black",
            stat.title.length > 4 && "text-3xl",
          )}
        >
          {stat.title}
        </span>
      </div>
      <div className="space-y-0.5">
        <p
          dangerouslySetInnerHTML={{ __html: stat._highlightResult?.subTitle?.value || stat.subTitle }}
          className="line-clamp-2 font-bold text-xl"
        />
        {(stat._snippetResult?.comments?.value || stat._highlightResult?.comments?.value) && (
          <div
            className="line-clamp-1 text-base"
            dangerouslySetInnerHTML={{
              __html: stat._snippetResult?.comments?.value || stat._highlightResult?.comments?.value || "",
            }}
          />
        )}
        <div className="flex items-center space-x-4">
          <p className="text-sm opacity-70">{stat.source}</p>
          <p className="text-sm uppercase opacity-70">
            {stat.regions?.[0]?.title || "Global"}
            {stat.industries[0] && <> · {stat.industries[0].title}</>}
          </p>
        </div>
      </div>
    </Link>
  )
}
function ReportHit(props: { hit: AlgoliaReport & ContentHit<AlgoliaReport> }) {
  const { hit: report } = props

  return (
    <Link
      className="flex items-center space-x-2 hover:opacity-75"
      href={
        report.downloadPdf
          ? `/reports/${report.objectID}?queryId=${props.hit.__queryID}`
          : createAssetUrl(report.downloadPpt) || ""
      }
    >
      <Image
        className="max-h-[100px] min-h-[100px] min-w-[150px] max-w-[150px] rounded-lg bg-gray-50 object-cover"
        width={150}
        height={100}
        alt="inno cover"
        src={report.cover || ""}
      />
      <div>
        <p
          className="whitespace-nowrap font-bold text-xl"
          dangerouslySetInnerHTML={{ __html: report._highlightResult?.title?.value || report.title }}
        />
        <div
          className="line-clamp-1"
          dangerouslySetInnerHTML={{
            __html:
              report._snippetResult?.description?.value ||
              report._highlightResult?.description?.value ||
              report.description ||
              "",
          }}
        />
        <div className="flex items-center space-x-1 text-sm opacity-75">
          {/* Temporarily keeping old type field for now, otherwise have to write script to update all reports in algolia */}
          <p>{report.types?.map((type) => REPORT_TYPE[type]).join(" · ") || (report.type && REPORT_TYPE[report.type])}</p>
          <p>·</p>
          <p>{dayjs.unix(report.publishedAt).format("D MMM YYYY")}</p>
        </div>
      </div>
    </Link>
  )
}
function DailyHit(props: { hit: AlgoliaDaily & ContentHit<AlgoliaDaily> }) {
  const { hit: daily } = props

  return (
    <Link
      className="flex items-center space-x-2 overflow-hidden hover:opacity-75"
      href={`/daily/${daily.slug}?queryId=${props.hit.__queryID}`}
    >
      <Image
        className="max-h-[100px] min-h-[100px] min-w-[150px] max-w-[150px] rounded-lg bg-gray-50 object-cover"
        width={150}
        height={100}
        alt="cover"
        src={daily.cover || ""}
      />
      <div className="space-y-1">
        <p
          className="max-w-full truncate whitespace-nowrap font-semibold text-xl"
          dangerouslySetInnerHTML={{ __html: daily._highlightResult?.title?.value || daily.title }}
        />

        <div
          className="line-clamp-1"
          dangerouslySetInnerHTML={{
            __html:
              daily._snippetResult?.description?.value || daily._highlightResult?.description?.value || daily.description || "",
          }}
        />
        <div className="flex items-center space-x-2">
          <div
            className="flex w-max items-center rounded-full px-2 py-0.5"
            style={{
              background: `linear-gradient(90deg, ${
                daily.type === "INNOVATION"
                  ? "#8AE9C1"
                  : daily.type === "INSIGHT"
                    ? "#DE99FF"
                    : daily.type === "TREND"
                      ? "#FFBECB"
                      : daily.type === "STAT"
                        ? "#7DA4FF"
                        : "#FE8066"
              } 0%, #D2EBFF 100%)`,
            }}
          >
            <Image
              width={12}
              height={12}
              className="mr-1"
              alt="type icon"
              src={`/${
                daily.type === "INNOVATION"
                  ? "innovation"
                  : daily.type === "INSIGHT"
                    ? "insight"
                    : daily.type === "TREND"
                      ? "trend"
                      : daily.type === "STAT"
                        ? "stat"
                        : "thought-starter"
              }-icon.png`}
            />

            <p className="font-semibold text-black text-sm">
              {daily.type === "INNOVATION"
                ? "Innovation"
                : daily.type === "INSIGHT"
                  ? "Insight"
                  : daily.type === "TREND"
                    ? "Trend"
                    : daily.type === "STAT"
                      ? "Stat"
                      : "Thought-starter"}
            </p>
          </div>
          <p className="text-sm">{dayjs.unix(daily.publishedAt).format("D MMM YYYY")}</p>
        </div>
      </div>
    </Link>
  )
}
function IndustryHit(props: { hit: AlgoliaIndustry & ContentHit<AlgoliaIndustry> }) {
  const { hit: industry } = props
  return (
    <Link
      className="flex items-center space-x-1 hover:opacity-75"
      href={`/industries/${industry.slug}?queryId=${props.hit.__queryID}`}
    >
      <div className="w-max">
        <ContentIcon icon={createAssetUrl(industry.iconImage)} size={100} className="rounded-none p-3" />
      </div>
      <div>
        <p
          className="whitespace-nowrap font-bold text-xl"
          dangerouslySetInnerHTML={{ __html: industry._highlightResult?.title?.value || industry.title }}
        />
        <p
          className="line-clamp-1"
          dangerouslySetInnerHTML={{
            __html: industry._snippetResult?.description?.value || industry.description || "",
          }}
        />
      </div>
    </Link>
  )
}

function NoResultsBoundary({
  children,
  shouldShowNoResults,
  index,
  component,
}: {
  shouldShowNoResults?: boolean
  index: string
  component: React.JSXElementConstructor<{
    hit: Hit<any>
    sendEvent: SendEventForHits
  }>
  children: React.ReactNode
}) {
  const { results } = useInstantSearch()
  if (!results.__isArtificial && results.nbHits === 0) {
    if (shouldShowNoResults) return null
    // const query = indexUiState.query || "" // for some reason indexUiState.query is always undefined here???
    return (
      <div className="w-full space-y-4">
        <div className="flex min-h-[40px] items-center justify-center">
          <div className="flex max-w-xl flex-col items-center space-y-4 text-center">
            <Image
              width={300}
              height={200}
              className="w-[40%] min-w-[200px] max-w-[300px] object-cover"
              src="/no-results.png"
              alt="no results"
            />
            <p className="text-gray-400">
              Oops! We didn't find anything for your search. Take a look at the below suggestions, you might find what you're
              looking for!
            </p>
          </div>
        </div>
        <hr />
        <p className="font-semibold">Here are some that may interest you!</p>
        <Index indexName={index}>
          <Configure
            hitsPerPage={6}
            query={""} // empty string to show most recent
            clickAnalytics
            analytics
            enableReRanking
            advancedSyntax
          />

          <Hits
            classNames={{ list: "grid grid-cols-1 gap-4 md:grid-cols-2" }}
            // @ts-ignore
            hitComponent={component}
          />
        </Index>
      </div>
    )
  }
  return <>{children}</>
}

function NoResultsBoundaryGlobal({ children }: { children: React.ReactNode }) {
  const { results, indexUiState } = useInstantSearch()

  if (!results.__isArtificial && results.nbHits === 0) {
    const query = indexUiState.query || ""
    return (
      <div className="space-y-2">
        <div className="flex min-h-[300px] items-center justify-center">
          <div className="flex max-w-xl flex-col items-center gap-4 text-center">
            <Image
              width={300}
              height={200}
              className="w-[40%] min-w-[200px] max-w-[300px] object-cover"
              src="/no-results.png"
              alt="no results"
            />
            <p className="opacity-65">
              Oops! We didn't find anything for "{query}". Take a look at the below suggestions, you might find what you're
              looking for!
            </p>
          </div>
        </div>
        <hr />
        <p className="font-semibold">Here are some that may interest you!</p>
        <Index indexName="innovations">
          <Configure
            hitsPerPage={6}
            query={""} // empty string to show most recent
            clickAnalytics
            analytics
            enableReRanking
            advancedSyntax
          />

          <Hits
            classNames={{ list: "grid grid-cols-1 gap-4 md:grid-cols-2" }}
            // @ts-ignore
            hitComponent={InnovationHit}
          />
        </Index>
      </div>
    )
  }
  return <>{children}</>
}
